import React, { useState } from "react";
import { useApp, Customer } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import PhoneInput from "@/components/PhoneInput";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { formatPhoneNumber } from "@/lib/utils";
import { toast } from "react-hot-toast";
import { Users, Phone, Edit, Trash2, CheckCircle, XCircle, Clock, Badge, Calendar, QrCode, Loader2 } from "lucide-react";
import CustomerQRCode from "./CustomerQRCode";

const CustomerManager: React.FC = () => {
  const { customers, editCustomer, deleteCustomer, hasPermission } = useApp();
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [isSaving, setIsSaving] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  
  const filteredCustomers = customers.filter(customer => {
    // Search filter
    const matchesSearch = customer.phoneNumber.includes(searchQuery.replace(/\D/g, "")) ||
                         (customer.name?.toLowerCase().includes(searchQuery.toLowerCase()) || false) ||
                         (customer.email?.toLowerCase().includes(searchQuery.toLowerCase()) || false);
    
    // Tab filter                     
    if (activeTab === "all") return matchesSearch;
    if (activeTab === "rewards") {
      return matchesSearch && customer.redeemedRewards.length > 0;
    }
    return matchesSearch;
  });
  
  const handleEdit = (customer: Customer) => {
    setEditingCustomer({ ...customer });
    setIsSheetOpen(true);
  };
  
  const handleSaveEdit = async () => {
    if (!editingCustomer) return;
    
    try {
      setIsSaving(true);
      const success = await editCustomer(editingCustomer.id, editingCustomer);
      if (success) {
        setIsSheetOpen(false);
      setEditingCustomer(null);
      }
    } catch (error) {
      console.error("Error saving customer:", error);
      toast.error("Failed to save changes. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = (id: string) => {
    setDeleteConfirmId(id);
  };

  const confirmDelete = () => {
    deleteCustomer(deleteConfirmId);
    setDeleteConfirmId("");
  };
  
  // Format a date for display
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };
  
  const hasManagePermission = hasPermission("manage_customers");
  
  // Sort customers with most visits first
  const sortedCustomers = [...filteredCustomers].sort((a, b) => {
    return b.visits.length - a.visits.length;
  });

  return (
    <Card className="border-t-4 border-t-restaurant-primary shadow-lg">
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-restaurant-primary" />
            <CardTitle className="text-lg sm:text-xl">Customer Management</CardTitle>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <Input
              placeholder="Search customers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full sm:w-60 border-restaurant-border focus-visible:ring-restaurant-primary"
            />
            
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full sm:w-auto">
              <TabsList className="bg-gray-100 w-full sm:w-auto">
                <TabsTrigger 
                  value="all"
                  className="flex-1 sm:flex-none data-[state=active]:bg-white data-[state=active]:text-restaurant-primary"
                >
                  All Customers
                </TabsTrigger>
                <TabsTrigger 
                  value="rewards"
                  className="flex-1 sm:flex-none data-[state=active]:bg-white data-[state=active]:text-restaurant-primary"
                >
                  With Rewards
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="p-0">
        {sortedCustomers.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground flex flex-col items-center gap-2">
            <Users className="h-12 w-12 text-muted-foreground/50" />
            <p className="text-lg">No customers found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold">Customer</TableHead>
                  <TableHead className="font-semibold text-center">Visits</TableHead>
                  <TableHead className="font-semibold hidden sm:table-cell">Last Visit</TableHead>
                  {hasManagePermission && (
                    <TableHead className="text-right font-semibold">Actions</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedCustomers.map((customer) => (
                  <TableRow key={customer.id} className="hover:bg-muted/30">
                    <TableCell className="font-medium">
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-restaurant-primary" />
                          <span>{formatPhoneNumber(customer.phoneNumber)}</span>
                        </div>
                        {customer.name && (
                          <span className="text-sm">{customer.name}</span>
                        )}
                        {customer.email && (
                          <span className="text-xs text-muted-foreground">{customer.email}</span>
                        )}
                        <span className="text-xs text-muted-foreground block sm:hidden mt-1">
                          Last visit: {customer.visits.length > 0
                            ? new Date(customer.visits[0].date).toLocaleDateString()
                            : "Never"}
                        </span>
                      </div>
                    </TableCell>
                    
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <span className="bg-restaurant-primary/10 text-restaurant-primary px-2 py-0.5 rounded-full font-medium">
                          {customer.visits.length}
                        </span>
                        <Clock className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </TableCell>
                    
                    <TableCell className="hidden sm:table-cell">
                      {customer.visits.length > 0
                        ? new Date(customer.visits[0].date).toLocaleDateString()
                        : "Never"}
                    </TableCell>
                    
                    {hasManagePermission && (
                      <TableCell className="text-right space-x-1">
                        <div className="flex items-center justify-end gap-1">
                          <Sheet>
                            <SheetTrigger asChild>
                              <Button
                                variant="outline"
                                size="icon"
                                className="border-restaurant-primary/30 text-restaurant-primary w-8 h-8 rounded-full"
                              >
                                <QrCode className="h-4 w-4" />
                              </Button>
                            </SheetTrigger>
                            <SheetContent side="right" className="sm:max-w-md w-[95vw]">
                              <SheetHeader className="mb-5">
                                <SheetTitle className="flex items-center gap-2">
                                  <QrCode className="h-5 w-5 text-restaurant-primary" /> 
                                  Customer QR Code
                                </SheetTitle>
                                <SheetDescription>
                                  Scan this QR code to quickly access this customer
                                </SheetDescription>
                              </SheetHeader>
                              
                              <CustomerQRCode 
                                customerId={customer.id}
                                customerName={customer.name}
                              />
                            </SheetContent>
                          </Sheet>
                          
                          <Sheet open={isSheetOpen && editingCustomer?.id === customer.id} onOpenChange={(open) => {
                            setIsSheetOpen(open);
                            if (!open) setEditingCustomer(null);
                          }}>
                            <SheetTrigger asChild>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleEdit(customer)}
                                className="border-restaurant-primary/30 text-restaurant-primary"
                              >
                                <Edit className="h-3.5 w-3.5 mr-1" />
                                Edit
                              </Button>
                            </SheetTrigger>
                            <SheetContent className="sm:max-w-md h-[100dvh] flex flex-col">
                              {editingCustomer && (
                                <>
                                  <SheetHeader>
                                    <SheetTitle className="flex items-center gap-2">
                                      <Phone className="h-5 w-5 text-restaurant-primary" />
                                      Edit Customer
                                    </SheetTitle>
                                    <SheetDescription>
                                      Update customer information
                                    </SheetDescription>
                                  </SheetHeader>
                                  <div className="py-6 space-y-5 flex-1 overflow-y-auto">
                                    <PhoneInput 
                                      value={editingCustomer.phoneNumber} 
                                      onChange={(value) => setEditingCustomer({
                                        ...editingCustomer,
                                        phoneNumber: value
                                      })} 
                                      label="Phone Number"
                                    />
                                    
                                    <div className="space-y-2">
                                      <Label htmlFor="customer-name">Name (Optional)</Label>
                                      <Input 
                                        id="customer-name"
                                        value={editingCustomer.name || ""}
                                        onChange={(e) => setEditingCustomer({
                                          ...editingCustomer,
                                          name: e.target.value
                                        })}
                                        placeholder="Customer name"
                                        className="border-restaurant-border focus-visible:ring-restaurant-primary"
                                      />
                                    </div>
                                    
                                    <div className="space-y-2">
                                      <Label htmlFor="customer-email">Email (Optional)</Label>
                                      <Input 
                                        id="customer-email"
                                        type="email"
                                        value={editingCustomer.email || ""}
                                        onChange={(e) => setEditingCustomer({
                                          ...editingCustomer,
                                          email: e.target.value
                                        })}
                                        placeholder="customer@example.com"
                                        className="border-restaurant-border focus-visible:ring-restaurant-primary"
                                      />
                                    </div>
                                    
                                    <Separator />
                                    
                                    <div className="pt-4 space-y-5">
                                      <div>
                                        <h3 className="font-medium flex items-center gap-2 mb-2">
                                          <Clock className="h-4 w-4 text-restaurant-primary" />
                                          Visit History
                                        </h3>
                                        <div className="bg-gray-50 p-4 rounded-md max-h-60 overflow-y-auto">
                                          {editingCustomer.visits.length === 0 ? (
                                            <p className="text-muted-foreground text-center text-sm py-2">
                                              No visits recorded yet
                                            </p>
                                          ) : (
                                            <div className="space-y-2">
                                              {editingCustomer.visits.slice().reverse().map(visit => (
                                                <div key={visit.id} className="flex justify-between text-sm">
                                                  <span>{formatDate(visit.date)}</span>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                      </div>
                                      </div>
                                      </div>
                                    </div>
                                    
                                  <div className="flex items-center justify-end gap-2 pt-4 mt-auto border-t">
                                    <Button 
                                      variant="outline" 
                                      onClick={() => {
                                        setIsSheetOpen(false);
                                        setEditingCustomer(null);
                                      }}
                                      disabled={isSaving}
                                    >
                                      Cancel
                                    </Button>
                                      <Button 
                                        onClick={handleSaveEdit}
                                      disabled={isSaving}
                                      className="bg-restaurant-primary hover:bg-restaurant-primary-hover"
                                    >
                                      {isSaving ? (
                                        <>
                                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                          Saving...
                                        </>
                                      ) : (
                                        'Save Changes'
                                      )}
                                      </Button>
                                  </div>
                                </>
                              )}
                            </SheetContent>
                          </Sheet>

                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="border-destructive/30 text-destructive"
                                onClick={() => handleDelete(customer.id)}
                              >
                                <Trash2 className="h-3.5 w-3.5 mr-1" />
                                Delete
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete this customer and all their visit history.
                                  This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                                <AlertDialogCancel className="mt-0">Cancel</AlertDialogCancel>
                                <AlertDialogAction 
                                  onClick={confirmDelete} 
                                  className="bg-restaurant-primary hover:bg-restaurant-primary-hover"
                                >
                                  Yes, delete customer
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CustomerManager;
